import { Sparkles, Zap } from 'lucide-react';

import { CustomerType } from '@/entities/customer-type';
import type { FeatureFlagChecker } from '@/hooks/use-feature-flag';
import { cls } from '@/utils/cls';

import { newsStyles } from '../../styles';
import type { News } from '../../types';

export const getFeatureFlaggedNews = (
  featureFlag: FeatureFlagChecker,
  customerType: CustomerType,
  isDemoCustomer: boolean,
): News[] => {
  const styles = newsStyles();

  const featureFlaggedNews: (boolean | News)[] = [
    !featureFlag('pcs') && customerType === CustomerType.BESS
      ? {
          key: 'pcs-analytics-upgrade-2025-05-23',
          // Slightly below "maintenance reminder" urgency but still eye-catching
          priority: 4,
          icon: <Zap className={cls(styles.icon(), 'text-yellow-300')} />,
          // Keep it visible for ~10 weeks; adjust if you prefer
          expiredAt: new Date('2025-08-01'),
          isCollapsible: true,
          label: (
            <>
              <a
                href="https://www.accure.net/battery-analytics-solutions/use-case---inverter-analytics"
                className="mb-2 font-semibold text-teal-700 hover:text-teal-800 hover:underline"
                target="_blank"
                rel="noreferrer"
              >
                Unlock PCS Analytics — full-stack insight beyond the battery!
              </a>
              <p>
                Extend your predictive lens to{' '}
                <strong>inverters &amp; transformers</strong> and
                <em> catch issues before they trip your site</em>. Early-warning
                analytics flag abnormal behaviour and prevent surprise
                shutdowns, while condition-based insights slash truck rolls and
                stretch equipment life.
              </p>

              <ul>
                <li>
                  <strong>End-to-end PCS visibility</strong> — track efficiency
                  from inverter to transformer.
                </li>
                <li>
                  <strong>Predictive maintenance</strong> — schedule fixes
                  before failure risk spikes.
                </li>
                <li>
                  <strong>
                    Auto-creates <em>Tasks</em>
                  </strong>{' '}
                  — actions land on your Kanban, not in email limbo.
                </li>
              </ul>

              <p>
                Curious? Reach out to your CS manager and see how fast you can
                go from "blind spot" to "bright idea".
              </p>
            </>
          ),
          thumbnail: {
            src: 'https://cdn.prod.website-files.com/67f3b2b2af7509d6ee127613/67f8f5dfe48bd1d4775a395b_image%209.avif',
            alt: 'PCS Analytics dashboard screenshot',
            href: 'https://www.accure.net/battery-analytics-solutions/use-case---inverter-analytics',
          },
        }
      : false,
    featureFlag('pcs')
      ? {
          key: 'announcement-on-platform-2025-02',
          priority: 1,
          icon: <Sparkles className="size-full text-yellow-400" />,
          expiredAt: new Date('2025-03-01'),
          label: (
            <>
              <h2 className={styles.primary({ className: 'mb-2' })}>
                Smarter Navigation, Better Insights – New Platform Update!
              </h2>
              <h4 className="font-semibold text-gray-800">New Features:</h4>
              <ul className="mb-2 list-disc pl-5">
                <li>
                  <strong>Enhanced Navigation:</strong> A redesigned interface
                  makes it easier to find what you need. Selecting an asset now
                  prefilters relevant features, improving workflow efficiency.
                </li>
                <li>
                  <strong>Advanced User Management:</strong> More control over
                  access with asset/site-level permissions, third-party access,
                  and granular feature-level control.
                </li>
                <li>
                  <strong>PCS Analytics Enhancements:</strong> Expanded insights
                  into Power Conversion Systems (PCS) with:
                  <ul className="mt-1 list-disc pl-5">
                    <li>
                      <strong>New PCS Alerts:</strong> Get notified when PCS
                      efficiency drops or power availability is at risk.
                    </li>
                    <li>
                      <strong>Dedicated PCS Dashboard:</strong> Track PCS
                      performance across sites, compare data, and analyze
                      historical trends.
                    </li>
                  </ul>
                </li>
              </ul>
            </>
          ),
        }
      : {
          key: 'announcement-on-platform-2025-02',
          priority: 1,
          icon: <Sparkles className="size-full text-yellow-400" />,
          expiredAt: new Date('2025-03-01'),
          label: (
            <>
              <h2 className={styles.primary({ className: 'mb-2' })}>
                Smarter Navigation, Better Insights – New Platform Update!
              </h2>
              <h4 className="font-semibold text-gray-800">New Features:</h4>
              <ul className="mb-2 list-disc pl-5">
                <li>
                  <strong>Enhanced Navigation:</strong> A redesigned interface
                  makes it easier to find what you need. Selecting an asset now
                  prefilters relevant features, improving workflow efficiency.
                </li>
                <li>
                  <strong>Advanced User Management:</strong> More control over
                  access with asset/site-level permissions, third-party access,
                  and granular feature-level control.
                </li>
              </ul>
            </>
          ),
        },
    featureFlag('newDataExplorer')
      ? {
          key: 'data-explorer-v2-launch-2026-02',
          priority: 3,
          icon: <Sparkles className="size-full text-yellow-400" />,
          expiredAt: new Date('2026-05-01'),
          excludedCustomers: ['17', '81'],
          isCollapsible: true,
          label: (
            <>
              <h2 className={cls(styles.primary(), 'mb-2')}>
                Introducing Data Explorer v2
              </h2>
              <p className="mb-2">
                A redesigned exploration experience with powerful comparison
                tools and shareable views — so you can diagnose issues faster
                and collaborate with your team.
              </p>

              <ul className="mb-2 list-disc pl-5">
                <li>
                  <strong>Compare side-by-side</strong> — select multiple
                  components at any level and overlay them on the same chart to
                  spot outliers instantly.
                </li>
                <li>
                  <strong>Shareable URLs</strong> — every filter selection is
                  saved in the URL; bookmark your view or share it with
                  colleagues.
                </li>
                <li>
                  <strong>Data quality hints</strong> — automatic detection of
                  flat-line periods with explanations, so you know when upstream
                  data may need attention.
                </li>
              </ul>

              <p>
                <strong>Try it now:</strong> Navigate to any asset and open{' '}
                <em>Data Explorer</em> from the main menu.
              </p>
            </>
          ),
        }
      : false,
    featureFlag('systemLogs') && !isDemoCustomer
      ? {
          key: 'system-logs-feature-2025-12',
          priority: 3,
          icon: <Sparkles className="size-full text-yellow-400" />,
          expiredAt: new Date('2026-02-01'),
          isCollapsible: true,
          label: (
            <>
              <a
                href="/system-logs"
                className={styles.primary({
                  className: 'mb-2 hover:underline',
                })}
              >
                Troubleshoot Faster with System Logs
              </a>
              <p className="mb-2">
                <strong>System Logs</strong> gives you direct access to your
                asset's technical event history. When something goes wrong or
                you need to trace system behavior, the logs are waiting for you
                — no support ticket needed.
              </p>

              <ul className="mb-2 list-disc pl-5">
                <li>
                  <strong>On-demand diagnostics</strong> — review system events
                  and error messages in real time.
                </li>
                <li>
                  <strong>Export for analysis</strong> — download logs to share
                  with your technical team or support.
                </li>
                <li>
                  <strong>Integrated across the platform</strong> — jump to logs
                  directly from <em>Alerts</em> (pre-filtered by component) or
                  the <em>3D Site Viewer</em> (shows log counts per asset).
                </li>
              </ul>

              <p>
                <strong>Where to find it:</strong> Navigate from the main menu,
                any asset overview page, alert details, or hover over components
                in the Site Viewer. Perfect for root-cause analysis or
                preemptive checks before maintenance windows.
              </p>
            </>
          ),
        }
      : false,
  ];
  return featureFlaggedNews.filter(Boolean) as News[];
};
